import _ from 'underscore';
import { ISize } from '../models/ISize';
import { Module } from '../Module';

export enum Corner {
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight
}

export interface IPosition {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export interface IStackedElement {
  contentElement: JQuery;
  getContentElementPosition: () => IPosition;
  destroy?(): void;
  setScope?(scope: ng.IScope): void;
}

export class StackedElement implements IStackedElement {

  private positionCorner: Corner;
  private positionElement: JQuery;
  private contentCorner: Corner;
  public contentElement: JQuery;

  private rendered: boolean;
  private scope: ng.IScope;
  private cornerPosition: IPosition;
  private updatePositionListener: (eventObject: JQuery.Event, ...args: any[]) => any;

  constructor(
      positionCorner: Corner, positionElement: JQuery,
      contentCorner: Corner, contentElement: JQuery)
  {
    this.positionCorner = positionCorner;
    this.positionElement = positionElement;
    this.contentCorner = contentCorner;
    this.contentElement = contentElement;

    this.rendered = false;

    // Binds updateContentElementPosition to this StackedElement instance and is a handle used to remove listeners
    this.updatePositionListener = () => {
      if(this.scope) {
        this.scope.$evalAsync(() => this.updateContentElementPosition());
      } else {
        this.updateContentElementPosition();
      }
    };

    this.positionElement.parents().on('scroll', this.updatePositionListener);
    $(window).on('resize', this.updatePositionListener);

    this.updateContentElementPosition();
  }

  public setScope(scope: ng.IScope) {
    this.scope = scope;
  }

  public destroy() {
    // Assumes the position element was never detached
    this.positionElement.parents().off('scroll', this.updatePositionListener);
    $(window).off('resize', this.updatePositionListener);
  }

  private getPositionX(positionCorner: Corner, positionOffset: JQueryCoordinates, positionSize: ISize): number {
    switch (positionCorner) {
      case Corner.TopLeft:
      case Corner.BottomLeft:
        return positionOffset.left;
      case Corner.TopRight:
      case Corner.BottomRight:
        return positionOffset.left + positionSize.width;
    }
  }

  private getPositionY(positionCorner: Corner, positionOffset: JQueryCoordinates, positionSize: ISize): number {
    switch (positionCorner) {
      case Corner.TopLeft:
      case Corner.TopRight:
        return positionOffset.top;
      case Corner.BottomLeft:
      case Corner.BottomRight:
        return positionOffset.top + positionSize.height;
    }
  }

  private getContentLeft(contentCorner: Corner, positionX: number, contentSize: ISize): number {
    switch (contentCorner) {
      case Corner.TopLeft:
      case Corner.BottomLeft:
        return positionX;
      case Corner.TopRight:
      case Corner.BottomRight:
        return positionX - contentSize.width;
    }
  }

  private getContentTop(contentCorner: Corner, positionY: number, contentSize: ISize): number {
    switch (contentCorner) {
      case Corner.TopLeft:
      case Corner.TopRight:
        return positionY;
      case Corner.BottomLeft:
      case Corner.BottomRight:
        return positionY - contentSize.height;
    }
  }

  private flipVertical(corner: Corner) {
    switch (corner) {
      case Corner.TopLeft:     return Corner.BottomLeft;
      case Corner.TopRight:    return Corner.BottomRight;
      case Corner.BottomLeft:  return Corner.TopLeft;
      case Corner.BottomRight: return Corner.TopRight;
    }
  }

  private flipHorizontal(corner: Corner) {
    switch (corner) {
      case Corner.TopLeft:     return Corner.TopRight;
      case Corner.TopRight:    return Corner.TopLeft;
      case Corner.BottomLeft:  return Corner.BottomRight;
      case Corner.BottomRight: return Corner.BottomLeft;
    }
  }

  private calculateDisplayPosition(
    positionCorner: Corner,
    positionSize: ISize,
    positionOffset: JQueryCoordinates,
    contentCorner: Corner,
    contentSize: ISize
  ): IPosition {
    const positionX = this.getPositionX(positionCorner, positionOffset, positionSize);
    const positionY = this.getPositionY(positionCorner, positionOffset, positionSize);
    const contentLeft = this.getContentLeft(contentCorner, positionX, contentSize);
    const contentTop = this.getContentTop(contentCorner, positionY, contentSize);

    return { left: contentLeft, top: contentTop };
  }

  private invalidHorizontalPosition(position: IPosition, contentWidth: number, windowWidth: number): boolean {
    return position.left  < 0 || position.left  > (windowWidth - contentWidth)
        || position.right < 0 || position.right > (windowWidth - contentWidth);
  }

  private invalidVerticalPosition(position: IPosition, contentHeight: number, windowHeight: number): boolean {
    return position.top    < 0  || position.top    > (windowHeight - contentHeight)
        || position.bottom < 0  || position.bottom > (windowHeight - contentHeight);
  }

  private getContentSize(): ISize {
    const contentSize = {
      height: this.contentElement.outerHeight(),
      width:  this.contentElement.outerWidth()
    };
    this.rendered = (contentSize.height > 0) && (contentSize.width > 0);
    return contentSize;
  }

  private getPositionSize(): ISize {
    return {
      height: this.positionElement.outerHeight(),
      width:  this.positionElement.outerWidth()
    };
  }

  private getWindowSize(): ISize {
    return {
      height: $(window).height(),
      width:  $(window).width()
    };
  }

  private updateContentElementPosition(): IPosition {

    // Initialize size variables
    let positionCorner = this.positionCorner;
    const positionSize = this.getPositionSize();
    const positionOffset = this.positionElement.offset();

    let contentCorner = this.contentCorner;
    const contentSize = this.getContentSize();

    const windowSize = this.getWindowSize();

    // Initialize helper functions
    const calculatePosition = () => this.calculateDisplayPosition(positionCorner, positionSize, positionOffset, contentCorner, contentSize);

    const flipHorizontal = () => {
      positionCorner = this.flipHorizontal(positionCorner);
      contentCorner = this.flipHorizontal(contentCorner);
      cornerPosition = calculatePosition();
    };

    const flipVertical = () => {
      positionCorner = this.flipVertical(positionCorner);
      contentCorner = this.flipVertical(contentCorner);
      cornerPosition = calculatePosition();
    };

    // Logic

    let cornerPosition = calculatePosition();

    if(this.invalidHorizontalPosition(cornerPosition, contentSize.width, windowSize.width)) {
      flipHorizontal();

      // If flipping to the opposite the corner didn't help, then flip back the preferred corner
      if(this.invalidHorizontalPosition(cornerPosition, contentSize.width, windowSize.width)) {
        flipHorizontal();
      }
    }

    if(this.invalidVerticalPosition(cornerPosition, contentSize.height, windowSize.height)) {
      flipVertical();

      // If flipping to the opposite the corner didn't help, then flip back the preferred corner
      if(this.invalidVerticalPosition(cornerPosition, contentSize.height, windowSize.height)) {
        flipVertical();
      }
    }

    this.cornerPosition = cornerPosition;
    return cornerPosition;
  }

  public getContentElementPosition(): IPosition {
    if(!this.rendered) this.updateContentElementPosition();
    return this.cornerPosition;
  }

}

export class StackedElementsService {

  public stackedElements: IStackedElement[];
  constructor() {
    this.stackedElements = [];
  }

  public static readonly instance = new StackedElementsService();

  public createStackedElement(
      positionCorner: Corner, positionElement: JQuery,
      contentCorner: Corner, contentElement: JQuery): IStackedElement
  {
    return this.addStackedElement(new StackedElement(positionCorner, positionElement, contentCorner, contentElement));
  }

  public addStackedElement(element: IStackedElement): IStackedElement {
    this.stackedElements.push(element);
    return element;
  }

  public removeStackedElement(element: IStackedElement) {
    const index = _.indexOf(this.stackedElements, element);
    if (index !== -1) {
      this.stackedElements.splice(index, 1);
    }
  }

  public removeAllStackedElements() {
    this.stackedElements.splice(0);
  }

}

export default StackedElementsService.instance;
